import React from 'react';
import { useTranslate } from 'react-admin';
import {
  Box, Card, Chip, CircularProgress, IconButton, Link, Skeleton, Stack,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { NetworkPing } from '@mui/icons-material';
import {
  BlockHeading, DataTable, DatagridEmpty, PageTitle, RecordCardList, SectionHeader, StatusDot,
} from '@/components/shell';
import { useKycServers, type KycServer } from '../hooks/useKycServers';
import { useSystemInfo } from '../hooks/useSystemInfo';
import { GatewayHealthCard } from './GatewayHealthCard';
import { HealthDot } from './HealthDot';
import { LatencyBar } from './LatencyBar';
import type { PingResult } from '../types';

const ServerState: React.FC<{ server: KycServer }> = ({ server }) => {
  const t = useTranslate();
  const isOnline = server.status === 'Online' && !server.isForcedOffline;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <HealthDot status={isOnline ? 'healthy' : 'down'} label={server.status} />
      {server.isForcedOffline && <Chip label={t('tech_support.servers.forcedOffline')} color="warning" size="small" />}
    </Box>
  );
};

/** The last measured response time, or why there is none. */
const PingOutcome: React.FC<{ ping?: PingResult }> = ({ ping }) => {
  const t = useTranslate();
  const theme = useTheme();
  if (!ping) return <Typography variant="body2" color="text.secondary" component="span">{t('screens.techSupport.notMeasured')}</Typography>;
  if (ping.status === 'reachable') return <LatencyBar latencyMs={ping.latencyMs} />;
  return (
    <Box>
      <StatusDot tone="problem" label={t('screens.techSupport.unreachable')} />
      {ping.error && (
        <Typography variant="body2" sx={{ color: theme.custom.status.problem.text }}>{ping.error}</Typography>
      )}
    </Box>
  );
};

const PingButton: React.FC<{ server: KycServer; pinging: boolean; onPing: (s: KycServer) => void }> = ({ server, pinging, onPing }) => {
  const t = useTranslate();
  const label = t('tech_support.servers.ping');
  return (
    <Tooltip title={label}>
      <span>
        <IconButton onClick={() => onPing(server)} disabled={pinging} aria-label={`${label}: ${server.name}`}>
          {pinging ? <CircularProgress size={18} color="inherit" /> : <NetworkPing fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const KycServicesMonitor: React.FC = () => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { servers, loading, pingResults, pingServer, pingingId, refresh } = useKycServers();
  const { systemInfo, loading: healthLoading, refresh: refreshHealth } = useSystemInfo();

  const empty = <DatagridEmpty title={t('tech_support.servers.empty')} hint={t('screens.techSupport.serversEmptyHint')} />;
  const title = t('screens.techSupport.registeredServers');

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('tech_support.tabs.servers')} subtitle={t('screens.techSupport.servicesSubtitle')} />}
        onRefresh={() => { refresh(); refreshHealth(); }}
        refreshing={loading}
      />

      <Box sx={{ mb: 3 }}>
        <GatewayHealthCard health={systemInfo?.gatewayHealth ?? null} loading={healthLoading} />
      </Box>

      <Box component="section" aria-labelledby="kyc-servers">
        <BlockHeading
          id="kyc-servers"
          title={title}
          subtitle={loading ? undefined : t('screens.techSupport.registeredCount', { smart_count: servers.length })}
        />
        {loading ? (
          <Card sx={{ p: 2 }}>
            <Stack>{[...Array(3)].map((_, i) => <Skeleton key={i} height={48} />)}</Stack>
          </Card>
        ) : isPhone ? (
          <RecordCardList
            records={servers}
            getKey={(s) => s.id}
            empty={empty}
            mobile={{
              primary: (s) => s.name,
              status: (s) => <ServerState server={s} />,
              lines: [
                { label: t('tech_support.servers.url'), render: (s) => s.url },
                { label: t('resources.servers.fields.port'), render: (s) => s.port },
                { label: t('tech_support.servers.latency'), render: (s) => <PingOutcome ping={pingResults[s.id]} /> },
              ],
              actions: (s) => <PingButton server={s} pinging={pingingId === s.id} onPing={pingServer} />,
            }}
          />
        ) : (
          <DataTable label={title} empty={servers.length === 0 ? empty : undefined}>
            <TableHead>
              <TableRow>
                <TableCell>{t('tech_support.servers.name')}</TableCell>
                <TableCell>{t('tech_support.servers.url')}</TableCell>
                <TableCell>{t('tech_support.servers.status')}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>{t('tech_support.servers.latency')}</TableCell>
                <TableCell align="right" aria-label={t('tech_support.servers.actions')} />
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.map((server) => (
                <TableRow key={server.id}>
                  <TableCell>
                    <Box>{server.name}</Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('screens.techSupport.port', { port: server.port })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Link href={server.url} target="_blank" rel="noopener">{server.url}</Link>
                  </TableCell>
                  <TableCell><ServerState server={server} /></TableCell>
                  <TableCell><PingOutcome ping={pingResults[server.id]} /></TableCell>
                  <TableCell align="right">
                    <PingButton server={server} pinging={pingingId === server.id} onPing={pingServer} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Box>
    </Box>
  );
};
